/**
 * useAccountMenuProps – Bridge hook that provides AccountMenu props
 * from auth context + portal context. Safe to use both inside and
 * outside ActivePortalProvider (falls back gracefully).
 */
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useMyProducers } from "@/hooks/useMyProducers";
import { useMyPortals } from "@/hooks/useMyPortals";
import { useMyInvitations } from "@/hooks/useMyInvitations";
import { supabase } from "@/integrations/supabase/client";
import type { AppMode } from "@/context/ActivePortalContext";

/**
 * Try to use ActivePortalContext if available (inside app routes),
 * otherwise derive a lightweight version from auth + portals hooks.
 */
export function useAccountMenuProps() {
  const navigate = useNavigate();
  const { user, isAdmin: authIsAdmin } = useAuth();
  // Detect if we're in admin mode from URL to avoid loading producers
  const isAdminContext = typeof window !== 'undefined' && (window.location.pathname.startsWith('/app/admin') || window.location.pathname.startsWith('/admin'));
  const { hasProducers, producers } = useMyProducers({ enabled: !isAdminContext });
  const { isEditor, isProducer: rpcIsProducer } = useMyPortals();
  const { invitations } = useMyInvitations();

  const pendingInvitations =
    invitations?.filter((i) => i.status === "pending").length ?? 0;

  const displayName =
    user?.user_metadata?.full_name || user?.email || "Du";

  const onSignOut = useCallback(async () => {
    await supabase.auth.signOut();
    navigate("/");
  }, [navigate]);

  // Lightweight mode setter for public headers (no ActivePortalProvider)
  const MODE_ROUTES: Record<AppMode, string> = {
    visitor: "/app/customer",
    host: "/app/producer",
    editor: "/app/editor",
    admin: "/app/admin/producers",
  };

  const onSetMode = useCallback(
    (mode: AppMode) => {
      navigate(MODE_ROUTES[mode]);
    },
    [navigate],
  );

  return {
    displayName,
    email: user?.email ?? null,
    isHost: isAdminContext ? rpcIsProducer : hasProducers,
    isEditor,
    isAdmin: authIsAdmin,
    firstProducerName: isAdminContext ? null : (producers[0]?.name ?? null),
    invitationCount: pendingInvitations,
    onSignOut,
    onSetMode,
    // activeMode will be "visitor" for public headers – the portal-aware
    // version can override this via ActivePortalContext
    activeMode: "visitor" as AppMode,
  };
}
